import React, { useContext, useState } from "react";
import _ from 'lodash'

import { CompetitorClaim } from "./external";
import { DateOrString } from "../utils/dates";
import { useParams } from "../navigation";
import { useExternal } from "./";
import { useExternalClaims } from "./ExternalClaimsProvider";

const ExternalMemberClaimsContext = React.createContext({
    competitorMemberId: -1 as number | undefined,
    competitorFamilyId: -1 as number | undefined,
    claimVersion: {version: '', versionDate: ''},
    availableVersions: [] as {version: string; versionDate: DateOrString}[],
    setClaimVersion: ({version, versionDate}: {version: string; versionDate: DateOrString}) => {},
    claims: [] as CompetitorClaim[],
    memberClaims: [] as CompetitorClaim[],
    familyClaims: [] as CompetitorClaim[],
    currentClaims: [] as CompetitorClaim[],
    addClaim: (claim: CompetitorClaim) => Promise.resolve({}),
    deleteClaim: (claim: CompetitorClaim) => Promise.resolve({}),
    updateClaim: (claim: CompetitorClaim) => Promise.resolve({}),
})

export function useExternalMemberClaims() {
    return useContext(ExternalMemberClaimsContext)
}

export function ExternalMemberClaimsProvider({children}) {
    const { internalReference } = useParams()
    const { memberByReference } = useExternal()
    const {aggregatedClaims, claims, postClaim, deleteClaim} = useExternalClaims()
    const member = memberByReference[internalReference ?? '']
    const competitorMemberId = member?.competitorMemberId
    const competitorFamilyId = member?.competitorFamilyId

    const familyClaims = _(aggregatedClaims[competitorFamilyId]?.claimsByMemberId ?? [])
        .flatMap(claimVersions => claimVersions.flatMap(v => v.claims)).value()

    const _memberClaims = aggregatedClaims[competitorFamilyId]?.claimsByMemberId[competitorMemberId] ?? []
    const memberClaims = _memberClaims.flatMap(v => v.claims)

    const availableVersions = _(_memberClaims).map(v => ({version: v.version, versionDate: v.versionDate})).reverse().value()

    const [_claimVersion, setClaimVersion] = useState(undefined)
    const fallBackVersion = availableVersions[0] ?? {version: '1', versionDate: new Date().toISOString().substring(0, 10)}
    const claimVersion = _claimVersion ?? fallBackVersion
    const currentClaims = _(memberClaims).filter(claim => claim.version === claimVersion.version).sortBy('claimNumber').value()

    const addClaim = async ({claimNumber, claimType, claimText, remark}: CompetitorClaim) => {
        return postClaim({...claimVersion, competitorMemberId, claimNumber, claimType, claimText, remark})
    }
    const updateClaim = async ({competitorClaimId, claimNumber, claimType, claimText, remark}: CompetitorClaim) => {
        return postClaim({...claimVersion, competitorMemberId, competitorClaimId, claimNumber, claimType, claimText, remark})
    }

    const value = {
        competitorMemberId,
        competitorFamilyId,
        availableVersions,
        claimVersion,
        setClaimVersion,
        claims,
        memberClaims,
        familyClaims,
        currentClaims,
        addClaim,
        deleteClaim,
        updateClaim,
    }

    return <ExternalMemberClaimsContext.Provider value={value}>
        {children}
    </ExternalMemberClaimsContext.Provider>
}
